import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import PageHeader from "../../../components/Common/PageHeader";
import TableFilter, {
  FilterField,
} from "components/Common/TableFilter/TableFilter";
import { Select } from "antd";
import { useSelector, useDispatch } from "react-redux";
import MyFeesTable from "../table/MyFeesTable";
import { useEffectiveStudentId } from "../../../components/Peoples/guardian/hooks/useEffectiveStudentId";
import { setSelectedStudentId } from "../../../redux/slicers/studentSlice";
import { RootState } from "../../../redux/store";
import useFee from "../hooks/useFee";
import NoDataComponent from "components/Common/NoData";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

const MyFees: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [filters, setFilters] = useState<{
    [key: string]: string | number | null;
  }>({});

  const {
    fees,
    loading,
    error,
    fetchFees,
    fetchFeeTypes,
    feeTypes,
    statusFee,
    infoPagination, 
  } = useFee();

  const user = useSelector((state: RootState) => state.authSlice.user);
  const students = useSelector((state: RootState) => state.student.students);
  const selectedStudentId = useSelector(
    (state: RootState) => state.student.selectedStudentId
  );
  const isGuardian = user?.role === "guardian";
  const studentId = useEffectiveStudentId();

  React.useEffect(() => {
    if (isGuardian && students.length > 0 && !selectedStudentId) {
      dispatch(setSelectedStudentId(students[0].id));
    }
  }, [dispatch, isGuardian, students, selectedStudentId]);

  useEffect(() => {
    if (studentId) {
      fetchFees({
        studentId,
        include: "feeType,feePayments",
        page: DEFAULT_PAGE,
        limit: DEFAULT_LIMIT,
      });
    }
  }, [studentId]);

  useEffect(() => {
    fetchFeeTypes();
  }, []);

  const breadcrumbItems = [
    { label: "dashboard", path: "/dashboard" },
    { label: "my_fees", active: true },
  ];

  const filterFields: FilterField[] = [
    { key: "search", label: t("search"), type: "text" },
    {
      key: "feeTypeId",
      label: t("type"),
      type: "select",
      options: feeTypes.map((feeType) => ({
        label: feeType.name,
        value: feeType.id,
      })),
    },
    {
      key: "status",
      label: t("status"),
      type: "select",
      options: statusFee,
    },
  ];

  const handleApplyFilters = (newFilters: {
    [key: string]: string | number | null;
  }) => {
    setFilters(newFilters);
    if (!studentId) return;

    fetchFees({
      studentId,
      include: "feeType,feePayments",
      page: 1,
      limit: infoPagination?.limit || DEFAULT_LIMIT,
      ...newFilters,
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    if (!studentId) return;

    fetchFees({
      studentId,
      include: "feeType,feePayments",
      page: 1,
      limit: infoPagination?.limit || DEFAULT_LIMIT,
    });
  };

  if (error) {
    return (
      <div className="alert alert-danger">{t("failed_to_fetch_fees")}</div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader title={t("my_fees")} breadcrumbItems={breadcrumbItems} />

        {/* Filter + student select */}
        <div className="d-flex align-items-center mb-3 gap-2 flex-wrap justify-content-end">
          {isGuardian && (
            <Select
              style={{ width: 250 }}
              value={selectedStudentId || undefined}
              placeholder={t("select_student_first")}
              onChange={(value) => {
                dispatch(setSelectedStudentId(value));
                const params: any = {
                  studentId: value,
                  include: "feeType,feePayments",
                  page: 1,
                  limit: infoPagination?.limit || DEFAULT_LIMIT,
                  ...filters,
                };
                fetchFees(params);
              }}
              options={students.map((student: any) => ({
                label: `${student.firstName} ${student.lastName}`,
                value: student.id,
              }))}
              className="me-2"
              notFoundContent={<NoDataComponent />}
            />
          )}
          <div>
            <TableFilter
              fields={filterFields}
              filters={filters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>
        </div>

        {!studentId && isGuardian ? (
          <p className="text-center text-danger">{t("select_student_first")}</p>
        ) : (
          <div className="table-responsive">
            <MyFeesTable
              tableData={fees}
              loading={loading}
              dataPaginate={{
                current: infoPagination?.page || DEFAULT_PAGE,
                pageSize: infoPagination?.limit || DEFAULT_LIMIT,
                total: infoPagination?.total || 0,
              }}
              onChange={(pg) => {
                if (!studentId) return;
                fetchFees({
                  studentId,
                  include: "feeType,feePayments",
                  ...filters,
                  page: pg.current || DEFAULT_PAGE,
                  limit: pg.pageSize || DEFAULT_LIMIT,
                });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MyFees;
